// główny layout
// @ts-nocheck

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { ChevronUp } from "react-bootstrap-icons";
import classnames from "classnames";

import { useRWD } from "hooks";
import { useDispatch, useSelector } from "store";
import { reduxActions } from "store";
import {
  Footer,
  HeaderMain,
  HeaderBottomBar,
  HeaderTopBar,
  BottomMenu,
} from "components/layouts/MainLayout/components";
import HeaderTopInfo from "components/layouts/MainLayout/components/HeaderTopInfo/HeaderTopInfo";
import DockedChatContainer from "components/containers/DockedChatContainer";
import MobileNavigationOverlay from "components/containers/MobileNavigationOverlay/MobileNavigationOverlay";

import styles from "theme/components/layouts/MainLayout/Main.module.scss";

const Main = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { profile, isOpenProfileReady, isAuthenticated } = useSelector(
    (state) => state.auth
  );
  const { isMobileMenu, isChatDocked, isChatOpen } = useSelector(
    (state) => state.ui
  );

  const withDockedChat = isChatDocked && isChatOpen;

  const { isMobile } = useRWD();

  // ref głównego kontentu aplikacji
  const scrollableContentRef = useRef<HTMLDivElement>(null);

  // czy jest widoczny przycisk powrotu na górę strony
  const [isBackToTopButton, setIsBackToTopButton] = useState(false);

  // funkcja skrolująca stronę do góry
  const scrollToTop = useCallback(
    () =>
      scrollableContentRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      }),
    [scrollableContentRef.current]
  );

  // przeskrolowanie strony do góry przy przejściu między stronami
  useEffect(() => {
    scrollToTop();
  }, [scrollableContentRef.current, location.key]);

  const pathSegments = location.pathname.split("/").filter((item) => item);
  const isAuthPage =
    pathSegments.includes("login") ||
    pathSegments.includes("forgot-password") ||
    pathSegments.includes("reset-password");
  const isDashboard = location.pathname.includes("dashboard");
  // Na mobile: pełny header (z wyszukiwarką) jak na pozostałych stronach – także na dashboardzie
  const showFullHeader =
    !isMobile || pathSegments.length < 2 || isAuthPage || isDashboard;

  const renderHeader = () => {
    if (showFullHeader) {
      return (
        <>
          <HeaderTopInfo />
          <HeaderTopBar />
          <header>
            <HeaderMain />
            <HeaderBottomBar />
          </header>
        </>
      );
    }

    // Na mobile przy głębszych ścieżkach (np. listing) pełny header jest ukryty,
    // ale komunikat HEADER_MESSAGE ma się wyświetlać wszędzie (zgodnie z Layout: Desktop + Mobile)
    return <HeaderTopInfo />;
  };

  const renderContent = () => (
    <div
      ref={scrollableContentRef}
      onScroll={() =>
        scrollableContentRef.current?.scrollTop &&
        scrollableContentRef.current?.scrollTop > 500
          ? setIsBackToTopButton(true)
          : setIsBackToTopButton(false)
      }
      // Ta klasa jest używana w innych komponentach - nie zmieniać!
      className={classnames("scrollableContent", styles.content)}
    >
      <main>
        <Outlet />
      </main>
      <footer>
        <Footer />
      </footer>
      <BottomMenu />
    </div>
  );

  const renderMenuPopup = () => {
    if (isMobile && isMobileMenu) {
      return (
        <MobileNavigationOverlay
          close={() => dispatch(reduxActions.setIsMobileMenu(false))}
          initialTab="categories"
        />
      );
    }

    return null;
  };

  const renderDockedChat = () => {
    if (withDockedChat && profile?.role !== "ROLE_OPEN_PROFILE") {
      return <DockedChatContainer />;
    }

    return null;
  };

  const renderBackToTopButton = () => {
    if (isBackToTopButton) {
      return (
        <button onClick={scrollToTop} className={styles.backToTop}>
          <ChevronUp />
        </button>
      );
    }

    return null;
  };

  // INHB-851: bramka bootstrapu open-profile (gość).
  // Na zimnym starcie (bot/prerender, brak JWT w localStorage) token gościa pobierany jest
  // asynchronicznie (POST /auth/login). Bez tej bramki strony i nagłówek montują się od razu
  // i odpalają zapytania do API ZANIM token jest gotowy → 401. Interceptor axios ponawia 401
  // tylko gdy isAuthenticated === true, więc te zapytania nie są ponawiane → pusta treść i brak
  // danych strukturalnych (JSON-LD) dla Googlebota. Wstrzymujemy render layoutu (a więc i
  // zapytań jego dzieci) do czasu uzyskania tokenu — wtedy zapytania lecą już z tokenem i strona
  // renderuje pełną treść, także w prerenderze dla SEO. Bramka aktywuje się WYŁĄCZNIE gdy
  // skonfigurowany jest open-profile i brak tokenu (zimny start) — dla zalogowanych i ciepłego
  // startu jest nieaktywna, więc nie zmienia działania dla zwykłych użytkowników.
  if (isOpenProfileReady && !isAuthenticated) {
    return (
      <div
        className={classnames(
          styles.componentWrapper,
          "StylePath-Components-Layouts-MainLayout-Main"
        )}
      />
    );
  }

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        "StylePath-Components-Layouts-MainLayout-Main",
        {
          [styles.withDockedChat]: withDockedChat,
        }
      )}
    >
      <div className={styles.mainWrapper}>
        <div className={styles.headerAndContent}>
          {renderHeader()}
          {renderContent()}
        </div>
        {renderDockedChat()}
      </div>
      {renderMenuPopup()}
      {renderBackToTopButton()}
    </div>
  );
};

export default Main;
